import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva } from "class-variance-authority";

import { cn } from "lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Match original "solid" variant - primary colored background
        solid:
          "bg-indigo-600 text-white shadow hover:bg-indigo-500 active:bg-indigo-700",
        // Match original "default" variant - white/gray background with border
        default:
          "bg-white border border-gray-300 text-gray-600 dark:bg-gray-700 dark:border-gray-700 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600 active:bg-gray-100 dark:active:bg-gray-500",
        // Match original "plain" variant - transparent background
        plain:
          "bg-transparent border border-transparent text-gray-600 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600 active:bg-gray-100 dark:active:bg-gray-500",
        // Match original "twoTone" variant
        twoTone:
          "bg-indigo-50 text-indigo-600 dark:bg-indigo-500 dark:bg-opacity-20 dark:text-indigo-50 hover:bg-indigo-100 dark:hover:bg-indigo-500 dark:hover:bg-opacity-30",
        // Additional variants for compatibility
        destructive:
          "bg-red-600 text-white shadow hover:bg-red-500 active:bg-red-700",
        outline:
          "border border-gray-300 bg-transparent shadow-sm hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-100",
        ghost: "hover:bg-gray-100 hover:text-gray-900 dark:hover:bg-gray-800 dark:hover:text-gray-100",
        link: "text-indigo-600 underline-offset-4 hover:underline dark:text-indigo-400",
      },
      size: {
        default: "h-11 px-8 py-2 text-sm",
        sm: "h-9 px-3 py-2 text-sm",
        lg: "h-11 px-8 py-2 text-base",
        xs: "h-8 px-3 py-1 text-xs",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props} />
  );
})
Button.displayName = "Button"

export { Button, buttonVariants }
